import { useVatRegistrationQuery } from "Api/Queries/Party/useVatRegistrationQuery";
import { BusinessInformationStepContent } from "Components/Onboarding/BusinessEntity/Steps/BusinessInformationStep/BusinessInformationStepContent";
import { CenteredCircularProgress } from "Components/Shared/CenteredCircularProgress";
import { ErrorText } from "Components/Shared/Errors/ErrorText";
import { useCurrentUserParty } from "Hooks/useCurrentUserParty";
import { useLoggedInUser } from "Hooks/useLoggedInUser";
import type { PartyDto, VatRegistrationDto } from "Infrastructure/Api/Api";
import { isVatQuestionOpen } from "Schemas/vatNumberSchema";
import { Resources, useResource } from "Translations/Resources";
import { useState } from "react";

type Props = {
  onNextStepRequested: () => void;
};

export const BusinessInformationStep = ({ onNextStepRequested }: Props) => {
  const { data: party, isLoading } = useCurrentUserParty();
  const { t } = useResource();

  if (isLoading) {
    return <CenteredCircularProgress color="grey" />;
  }

  if (!party) {
    return (
      <ErrorText>
        {t(
          Resources.Onboarding.BusinessOnboarding.Steps.BusinessInformation
            .LoadingError,
        )}
      </ErrorText>
    );
  }

  return (
    <BusinessInformationStepWithVatRegistration
      party={party}
      onNextStepRequested={onNextStepRequested}
    />
  );
};

type WithVatRegistrationProps = {
  party: PartyDto;
  onNextStepRequested: () => void;
};

// What the VAT register says about the numbers the step opens with, so the form shows the answer instead
// of asking the user. Captured once on mount: the submit itself changes the region (set-region) and the
// numbers (the save fills an SK IČ DPH), and re-keying the lookup then would swap the form for a spinner
// mid-submit. A status the save already verified is used as is — like the backend, nobody asks twice.
const BusinessInformationStepWithVatRegistration = ({
  party,
  onNextStepRequested,
}: WithVatRegistrationProps) => {
  const user = useLoggedInUser();
  const [storedRegistration] = useState<VatRegistrationDto | undefined>(() =>
    isVatQuestionOpen(party.vatStatus)
      ? undefined
      : { vatNumber: party.vatNumber, vatStatus: party.vatStatus },
  );
  const [lookup] = useState(() => ({
    region: storedRegistration ? null : (party.region ?? user?.region),
    taxNumber: party.taxNumber ?? "",
    vatNumber: party.vatNumber,
  }));

  // A failed or skipped lookup leaves the question open; the backend gives up on a register after 5 s.
  const { data: lookedUpRegistration, isLoading } = useVatRegistrationQuery(
    lookup.region,
    lookup.taxNumber,
    lookup.vatNumber,
  );

  if (isLoading) {
    return <CenteredCircularProgress color="grey" />;
  }

  return (
    <BusinessInformationStepContent
      party={party}
      vatRegistration={storedRegistration ?? lookedUpRegistration}
      onNextStepRequested={onNextStepRequested}
    />
  );
};
